/** Pure install-prompt logic: platform and standalone detection, the dismissal cooldown and which banner to show. */

export type InstallPlatform = 'ios-safari' | 'ios-other' | 'other';
export type BannerKind = 'native' | 'ios' | null;

export const DISMISS_KEY = 'ethrhunt.installPrompt.dismissedAt';
export const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;

const IOS_DEVICE = /iPhone|iPad|iPod/;
// Chrome, Firefox, Edge, Opera, Google app and in-app browsers on iOS: none use Safari's Share → Add to Home Screen.
const IOS_NOT_SAFARI = /CriOS|FxiOS|EdgiOS|OPiOS|GSA|FBAN|FBAV|Instagram|Line\//;

/** iPadOS 13+ reports a desktop Mac UA, so a touch-capable "Macintosh" counts as iOS. */
export function detectPlatform(userAgent: string, maxTouchPoints: number): InstallPlatform {
  const ios = IOS_DEVICE.test(userAgent) || (userAgent.includes('Macintosh') && maxTouchPoints > 1);
  if (!ios) return 'other';
  return IOS_NOT_SAFARI.test(userAgent) ? 'ios-other' : 'ios-safari';
}

export function isStandalone(
  matchMedia: ((query: string) => { matches: boolean }) | undefined,
  nav: { standalone?: boolean } | undefined,
): boolean {
  return Boolean(matchMedia?.('(display-mode: standalone)').matches) || nav?.standalone === true;
}

type ReadStorage = Pick<Storage, 'getItem'>;
type WriteStorage = Pick<Storage, 'setItem'>;

/** True while less than 14 days have passed since a stored dismissal. Missing, garbage or future values do not count. */
export function isDismissed(storage: ReadStorage | null | undefined, now: number): boolean {
  let raw: string | null;
  try {
    raw = storage?.getItem(DISMISS_KEY) ?? null;
  } catch {
    return false;
  }
  if (raw == null || !/^\d+$/.test(raw)) return false;
  const at = Number(raw);
  if (at > now) return false;
  return now - at < DISMISS_COOLDOWN_MS;
}

/** Best effort: storage may be missing or throw (Safari private mode); the store keeps an in-memory flag either way. */
export function recordDismissal(storage: WriteStorage | null | undefined, now: number): void {
  try {
    storage?.setItem(DISMISS_KEY, String(now));
  } catch {
    // Dismissal then lasts for this page session only.
  }
}

export function bannerKind(s: {
  standalone: boolean;
  installed: boolean;
  hasDeferredPrompt: boolean;
  platform: InstallPlatform;
  dismissed: boolean;
}): BannerKind {
  if (s.standalone || s.installed || s.dismissed) return null;
  if (s.hasDeferredPrompt) return 'native';
  return s.platform === 'ios-safari' ? 'ios' : null;
}
